import { RhfDropdown } from '@taxbit-private/cosmic-react-hook-form'
import { useCallback, useMemo, useState } from 'react'

import { OrgByParent } from 'api/types/orgsApiTypes'
import { useOrgDisplayWithSlug } from 'hooks/utils/useOrgDisplayWithSlug'

import { OrgManagerFormValues } from './hooks/useOrgManagerFormMethods'

enum OrgSelectorDropdownTrackingId {
  Dropdown = 'org-selector-dropdown',
}

type OrgOption = {
  orgName: string
  displayName: string | null
  orgSlug: string | null | undefined
}

const filterOrgOptions = (
  options: OrgOption[],
  getLabel: (option: OrgOption) => string,
  getDescription: (option: OrgOption) => string,
  query: string
): OrgOption[] => {
  if (!query.trim()) {
    return options
  }
  const lowerQuery = query.toLowerCase()
  return options.filter((option) => {
    const labelMatch = getLabel(option).toLowerCase().includes(lowerQuery)
    const descMatch = getDescription(option).toLowerCase().includes(lowerQuery)
    const valueMatch = option.orgName.toLowerCase().includes(lowerQuery)
    return labelMatch || descMatch || valueMatch
  })
}

type OrgSelectorDropdownProps = {
  onOrgChange?: (orgId: string) => void
  orgs: OrgByParent[] | undefined
  isLoading: boolean
}

const OrgSelectorDropdown = ({ onOrgChange, orgs, isLoading }: OrgSelectorDropdownProps) => {
  const [orgSearchTerm, setOrgSearchTerm] = useState('')
  const { shouldShowSlug } = useOrgDisplayWithSlug()

  const getOrgOptionLabel = useCallback((option: OrgOption) => {
    return option.displayName ?? option.orgName
  }, [])
  const getOrgOptionDescription = useCallback(
    (option: OrgOption) => {
      if (shouldShowSlug && option.orgSlug) {
        return option.orgSlug
      }
      return ''
    },
    [shouldShowSlug]
  )
  const getOrgOptionValue = useCallback((option: OrgOption) => option.orgName, [])
  const getOrgOptionKey = useCallback((option: OrgOption) => option.orgName, [])

  const sortedOrgs = useMemo(() => {
    if (!orgs) {
      return []
    }
    return orgs.sort((a, b) =>
      (a.org_display_name ?? a.org_name).localeCompare(b.org_display_name ?? b.org_name)
    )
  }, [orgs])

  const orgOptions = useMemo(() => {
    return sortedOrgs.map(
      (org): OrgOption => ({
        orgName: org.org_name,
        displayName: org.org_display_name ?? null,
        orgSlug: org.org_slug,
      })
    )
  }, [sortedOrgs])

  const filteredOrgOptions = useMemo(
    () => filterOrgOptions(orgOptions, getOrgOptionLabel, getOrgOptionDescription, orgSearchTerm),
    [orgOptions, getOrgOptionLabel, getOrgOptionDescription, orgSearchTerm]
  )

  const handleChange = useCallback(
    (option: OrgOption) => {
      onOrgChange?.(option.orgName)
    },
    [onOrgChange]
  )

  return (
    <RhfDropdown<OrgManagerFormValues, 'orgId', OrgOption>
      label="Organization"
      name="orgId"
      getOptionValue={getOrgOptionValue}
      getOptionKey={getOrgOptionKey}
      getOptionLabel={getOrgOptionLabel}
      getOptionDescription={getOrgOptionDescription}
      options={filteredOrgOptions}
      trackingId={OrgSelectorDropdownTrackingId.Dropdown}
      shouldUsePortal={true}
      shouldAlignMenuToTargetWidth={true}
      isLoading={isLoading}
      onChange={handleChange}
      searchBarProps={{
        placeholder: 'Search...',
        value: orgSearchTerm,
        onTextChange: setOrgSearchTerm,
      }}
    />
  )
}

export default OrgSelectorDropdown
